import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { adminAPI } from '../services/api';
import {
  Box,
  Package,
  AlertTriangle,
  Plus,
  Edit2,
  Trash,
  Layers,
} from 'lucide-react';

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const Subtitle = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  max-width: 720px;
  line-height: 1.5;
`;

const Grid = styled.div`
  display: grid;
  gap: ${props => props.theme.spacing.lg};
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
`;

const Card = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: ${props => props.theme.spacing.lg};
  box-shadow: ${props => props.theme.shadows.sm};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const PackageTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const Description = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.5;
`;

const OptionsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 8px 0;
  color: ${props => props.theme.colors.textSecondary};
  font-weight: 600;
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const Td = styled.td`
  padding: 8px 0;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.text};
`;

const EmptyState = styled.div`
  padding: ${props => props.theme.spacing.xxl};
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px dashed ${props => props.theme.colors.border};
`;

const InfoBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${props => props.theme.spacing.sm};
  background: ${props => props.theme.colors.background};
  border: 1px dashed ${props => props.theme.colors.borderMuted};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.9rem;
`;

const ActionsBar = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${props => props.theme.colors.primary};
  border: none;
  color: white;
  padding: 10px 16px;
  border-radius: ${props => props.theme.borderRadius.md};
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.1s ease, box-shadow 0.1s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${props => props.theme.shadows.md};
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const CardActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.sm};
`;

const GhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.sm};
  padding: 8px 12px;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border 0.15s ease;

  &:hover {
    background: ${props => props.theme.colors.surfaceMuted};
    color: ${props => props.theme.colors.text};
  }

  &.danger {
    border-color: ${props => props.theme.colors.error};
    color: ${props => props.theme.colors.error};

    &:hover {
      background: rgba(239, 68, 68, 0.1);
    }
  }
`;

const OptionActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.xs};
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.35);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${props => props.theme.spacing.lg};
  z-index: 1000;
`;

const ModalContent = styled.form`
  width: min(760px, 100%);
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius.xl};
  box-shadow: ${props => props.theme.shadows.lg};
  padding: ${props => props.theme.spacing.xl};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
`;

const FormGrid = styled.div`
  display: grid;
  gap: ${props => props.theme.spacing.md};
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.9rem;
  color: ${props => props.theme.colors.textSecondary};
`;

const Input = styled.input`
  padding: 10px 12px;
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.surfaceMuted};
  color: ${props => props.theme.colors.text};
  font-size: 0.95rem;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
  }
`;

const TextArea = styled.textarea`
  padding: 10px 12px;
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.surfaceMuted};
  color: ${props => props.theme.colors.text};
  font-size: 0.95rem;
  min-height: 120px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
  }
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.95rem;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing.sm};
`;

const SecondaryButton = styled.button`
  padding: 10px 16px;
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.surfaceMuted};
  color: ${props => props.theme.colors.text};
  cursor: pointer;

  &:hover {
    background: ${props => props.theme.colors.surface};
  }
`;

function BillingPackages() {
  const queryClient = useQueryClient();
  const [packageModal, setPackageModal] = useState({ open: false, mode: 'create', pkg: null });
  const [packageForm, setPackageForm] = useState({
    package_key: '',
    name: '',
    description: '',
    category: '',
    is_active: true,
  });

  const [optionModal, setOptionModal] = useState({ open: false, pkg: null, option: null });
  const [optionForm, setOptionForm] = useState({
    option_index: '',
    label: '',
    price: '',
    currency: 'RUB',
    requests: '',
    generations: '',
  });

  const { data, isLoading, error } = useQuery(
    'admin.billing.packages',
    () => adminAPI.getBillingPackages().then(res => res.data),
    {
      refetchInterval: 60000,
    }
  );

  const upsertPackageMutation = useMutation(
    payload => adminAPI.upsertBillingPackage(payload).then(res => res.data),
    {
      onSuccess: () => {
        toast.success('Пакет сохранён');
        queryClient.invalidateQueries('admin.billing.packages');
        closePackageModal();
      },
      onError: err => {
        toast.error(err.response?.data?.detail || 'Не удалось сохранить пакет');
      },
    }
  );

  const deletePackageMutation = useMutation(
    packageId => adminAPI.deleteBillingPackage(packageId).then(res => res.data),
    {
      onSuccess: () => {
        toast.success('Пакет удалён');
        queryClient.invalidateQueries('admin.billing.packages');
      },
      onError: err => {
        toast.error(err.response?.data?.detail || 'Не удалось удалить пакет');
      },
    }
  );

  const upsertOptionMutation = useMutation(
    ({ packageId, payload }) =>
      adminAPI.upsertPackageOption(packageId, payload).then(res => res.data),
    {
      onSuccess: () => {
        toast.success('Опция сохранена');
        queryClient.invalidateQueries('admin.billing.packages');
        closeOptionModal();
      },
      onError: err => {
        toast.error(err.response?.data?.detail || 'Не удалось сохранить опцию');
      },
    }
  );

  const deleteOptionMutation = useMutation(
    optionId => adminAPI.deletePackageOption(optionId).then(res => res.data),
    {
      onSuccess: () => {
        toast.success('Опция удалена');
        queryClient.invalidateQueries('admin.billing.packages');
      },
      onError: err => {
        toast.error(err.response?.data?.detail || 'Не удалось удалить опцию');
      },
    }
  );

  const iconForType = pkgType => {
    if (pkgType === 'image') return <Package size={24} />;
    if (pkgType === 'video') return <Box size={24} />;
    return <Package size={24} />;
  };

  const openCreatePackageModal = () => {
    setPackageForm({
      package_key: '',
      name: '',
      description: '',
      category: '',
      is_active: true,
    });
    setPackageModal({ open: true, mode: 'create', pkg: null });
  };

  const openEditPackageModal = pkg => {
    setPackageForm({
      package_key: pkg.package_key,
      name: pkg.name,
      description: pkg.description || '',
      category: pkg.category || '',
      is_active: Boolean(pkg.is_active),
    });
    setPackageModal({ open: true, mode: 'edit', pkg });
  };

  const closePackageModal = () => {
    setPackageModal({ open: false, mode: 'create', pkg: null });
  };

  const handlePackageFormChange = evt => {
    const { name, value, type, checked } = evt.target;
    setPackageForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const submitPackageForm = evt => {
    evt.preventDefault();
    if (packageModal.mode === 'create' && !packageForm.package_key.trim()) {
      toast.error('Укажите package_key');
      return;
    }
    if (!packageForm.name.trim()) {
      toast.error('Введите название пакета');
      return;
    }

    const payload = {
      package_key: packageForm.package_key.trim(),
      name: packageForm.name.trim(),
      description: packageForm.description?.trim() || null,
      category: packageForm.category?.trim() || null,
      is_active: packageForm.is_active,
    };

    if (packageModal.mode === 'create') {
      upsertPackageMutation.mutate(payload);
    } else if (packageModal.pkg) {
      const { package_key, ...rest } = payload;
      upsertPackageMutation.mutate({ ...rest, package_key: packageModal.pkg.package_key });
    }
  };

  const openOptionModal = (pkg, option = null) => {
    setOptionForm({
      option_index: option ? option.option_index : '',
      label: option ? option.label : '',
      price: option ? option.price : '',
      currency: option ? option.currency : 'RUB',
      requests: option?.requests ?? '',
      generations: option?.generations ?? '',
    });
    setOptionModal({ open: true, pkg, option });
  };

  const closeOptionModal = () => {
    setOptionModal({ open: false, pkg: null, option: null });
  };

  const handleOptionFormChange = evt => {
    const { name, value } = evt.target;
    setOptionForm(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const submitOptionForm = evt => {
    evt.preventDefault();
    const index = Number(optionForm.option_index);
    if (!Number.isInteger(index) || index < 0) {
      toast.error('Укажите целый option_index ≥ 0');
      return;
    }
    if (!optionForm.label.trim()) {
      toast.error('Введите название опции');
      return;
    }
    if (!optionForm.price && optionForm.price !== 0) {
      toast.error('Введите цену');
      return;
    }

    const payload = {
      option_index: index,
      label: optionForm.label.trim(),
      price: Number(optionForm.price) || 0,
      currency: optionForm.currency || 'RUB',
      requests: optionForm.requests === '' ? null : Number(optionForm.requests),
      generations: optionForm.generations === '' ? null : Number(optionForm.generations),
    };

    upsertOptionMutation.mutate({
      packageId: optionModal.pkg.id,
      payload,
    });
  };

  const handleDeletePackage = pkg => {
    if (window.confirm(`Удалить пакет "${pkg.name}" вместе с опциями?`)) {
      deletePackageMutation.mutate(pkg.id);
    }
  };

  const handleDeleteOption = optionId => {
    if (window.confirm('Удалить опцию пакета?')) {
      deleteOptionMutation.mutate(optionId);
    }
  };

  const packages = useMemo(() => data?.packages ?? [], [data]);
  const isBusy =
    upsertPackageMutation.isLoading ||
    deletePackageMutation.isLoading ||
    upsertOptionMutation.isLoading ||
    deleteOptionMutation.isLoading;

  if (isLoading) {
    return <EmptyState>Загрузка пакетов...</EmptyState>;
  }

  if (error) {
    return (
      <EmptyState>
        <AlertTriangle size={18} /> Не удалось загрузить список пакетов
      </EmptyState>
    );
  }

  return (
    <Container>
      <Header>
        <Title>
          <Package size={32} />
          Пакеты генераций
        </Title>
        <Subtitle>
          Управляйте дополнительными пакетами для текста, изображений и видео. Настраивайте стоимость,
          описание и содержимое опций прямо из админки.
        </Subtitle>
      </Header>

      <InfoBox>
        <AlertTriangle size={18} />
        <div>
          <strong>Совет.</strong> Для текстовых пакетов задайте поле <code>requests</code>, для
          пакетов генераций — <code>generations</code>. Можно хранить оба поля, но WebApp использует
          только заполненные значения.
        </div>
      </InfoBox>

      <ActionsBar>
        <PrimaryButton onClick={openCreatePackageModal}>
          <Plus size={18} />
          Новый пакет
        </PrimaryButton>
      </ActionsBar>

      {packages.length ? (
        <Grid>
          {packages.map(pkg => (
            <Card key={pkg.id}>
              <PackageTitle>
                {iconForType(pkg.category)}
                {pkg.name}
              </PackageTitle>
              <Description>{pkg.description || 'Пакет без описания'}</Description>
              <OptionsTable>
                <thead>
                  <tr>
                    <Th>Опция</Th>
                    <Th>Количество</Th>
                    <Th>Цена</Th>
                    <Th></Th>
                  </tr>
                </thead>
                <tbody>
                  {pkg.options?.length ? (
                    pkg.options.map(option => (
                      <tr key={option.id}>
                        <Td>
                          <strong>{option.label}</strong>
                          <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                            index: {option.option_index}
                          </div>
                        </Td>
                      <Td>
                        {option.requests
                          ? `${option.requests} запросов`
                          : `${option.generations} генераций`}
                      </Td>
                      <Td>
                        {option.currency || 'RUB'} {option.price}
                      </Td>
                        <Td>
                          <OptionActions>
                            <GhostButton onClick={() => openOptionModal(pkg, option)}>
                              <Edit2 size={14} />
                            </GhostButton>
                            <GhostButton
                              className="danger"
                              onClick={() => handleDeleteOption(option.id)}
                            >
                              <Trash size={14} />
                            </GhostButton>
                          </OptionActions>
                        </Td>
                    </tr>
                    ))
                  ) : (
                    <tr>
                      <Td colSpan={4} style={{ color: '#6b7280' }}>
                        Опции не заданы
                      </Td>
                    </tr>
                  )}
                </tbody>
              </OptionsTable>
              <CardActions>
                <GhostButton onClick={() => openEditPackageModal(pkg)}>
                  <Edit2 size={16} />
                  Редактировать пакет
                </GhostButton>
                <GhostButton onClick={() => openOptionModal(pkg)}>
                  <Plus size={16} />
                  Добавить опцию
                </GhostButton>
                <GhostButton className="danger" onClick={() => handleDeletePackage(pkg)}>
                  <Trash size={16} />
                  Удалить пакет
                </GhostButton>
              </CardActions>
            </Card>
          ))}
        </Grid>
      ) : (
        <EmptyState>Пакеты не найдены</EmptyState>
      )}

      {packageModal.open && (
        <ModalOverlay>
          <ModalContent onSubmit={submitPackageForm}>
            <ModalTitle>
              {packageModal.mode === 'create' ? 'Новый пакет' : 'Редактирование пакета'}
            </ModalTitle>

            <FormGrid>
              {packageModal.mode === 'create' && (
                <Field>
                  Ключ пакета
                  <Input
                    name="package_key"
                    value={packageForm.package_key}
                    onChange={handlePackageFormChange}
                    placeholder="Например, text_requests"
                    required
                  />
                </Field>
              )}

              <Field>
                Название
                <Input
                  name="name"
                  value={packageForm.name}
                  onChange={handlePackageFormChange}
                  placeholder="GPT пакеты"
                  required
                />
              </Field>

              <Field>
                Категория
                <Input
                  name="category"
                  value={packageForm.category}
                  onChange={handlePackageFormChange}
                  placeholder="text | image | video"
                />
              </Field>
            </FormGrid>

            <Field>
              Описание
              <TextArea
                name="description"
                value={packageForm.description}
                onChange={handlePackageFormChange}
                placeholder="Краткое описание пакета"
              />
            </Field>

            <CheckboxRow>
              <input
                id="pkg-active"
                type="checkbox"
                name="is_active"
                checked={packageForm.is_active}
                onChange={handlePackageFormChange}
              />
              <label htmlFor="pkg-active">Пакет активен</label>
            </CheckboxRow>

            <ModalActions>
              <SecondaryButton type="button" onClick={closePackageModal} disabled={isBusy}>
                Отмена
              </SecondaryButton>
              <PrimaryButton type="submit" disabled={isBusy}>
                {packageModal.mode === 'create' ? 'Создать пакет' : 'Сохранить изменения'}
              </PrimaryButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {optionModal.open && (
        <ModalOverlay>
          <ModalContent onSubmit={submitOptionForm}>
            <ModalTitle>
              {optionModal.option
                ? `Редактирование опции (${optionModal.pkg.name})`
                : `Новая опция (${optionModal.pkg.name})`}
            </ModalTitle>

            <FormGrid>
              <Field>
                Индекс опции
                <Input
                  name="option_index"
                  type="number"
                  value={optionForm.option_index}
                  onChange={handleOptionFormChange}
                  min="0"
                  required
                />
              </Field>

              <Field>
                Заголовок
                <Input
                  name="label"
                  value={optionForm.label}
                  onChange={handleOptionFormChange}
                  placeholder="Например, 50 запросов"
                  required
                />
              </Field>

              <Field>
                Цена
                <Input
                  name="price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={optionForm.price}
                  onChange={handleOptionFormChange}
                  required
                />
              </Field>

              <Field>
                Валюта
                <Input
                  name="currency"
                  value={optionForm.currency}
                  onChange={handleOptionFormChange}
                  placeholder="RUB"
                />
              </Field>

              <Field>
                Запросов
                <Input
                  name="requests"
                  type="number"
                  min="0"
                  value={optionForm.requests}
                  onChange={handleOptionFormChange}
                  placeholder="Если пакет текстовый"
                />
              </Field>

              <Field>
                Генераций
                <Input
                  name="generations"
                  type="number"
                  min="0"
                  value={optionForm.generations}
                  onChange={handleOptionFormChange}
                  placeholder="Если пакет изображений/видео"
                />
              </Field>
            </FormGrid>

            <ModalActions>
              <SecondaryButton type="button" onClick={closeOptionModal} disabled={isBusy}>
                Отмена
              </SecondaryButton>
              <PrimaryButton type="submit" disabled={isBusy}>
                {optionModal.option ? 'Сохранить опцию' : 'Добавить опцию'}
              </PrimaryButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
}

export default BillingPackages;

